import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

import { METRIC_VALUE_ICONS } from './icon-const';
import { MetricValue } from './types';

const metricThresholds: Record<
  'mos' | 'packetLoss' | 'jitter', // Define the metrics we are interested in
  // direction is 'increasing' for metrics where higher values are worse (like packet loss and jitter),
  { direction: 'increasing' | 'decreasing'; thresholds: Record<'positive' | 'warn', number>; suffix: string }
> = {
  mos: {
    direction: 'decreasing',
    thresholds: {
      positive: 4,
      warn: 3,
    },
    suffix: ' / 5',
  },
  packetLoss: {
    direction: 'increasing',
    thresholds: {
      positive: 5,
      warn: 20,
    },
    suffix: '%',
  },
  jitter: {
    direction: 'increasing',
    thresholds: {
      positive: 10,
      warn: 50,
    },
    suffix: ' ms',
  },
} as const;

function calculateMetricSentiment(
  metricType: 'mos' | 'packetLoss' | 'jitter',
  value: number
): keyof typeof METRIC_VALUE_ICONS {
  const { direction, thresholds } = metricThresholds[metricType];

  if (direction === 'decreasing') {
    // For metrics where lower values are worse (like MOS)
    if (value >= thresholds.positive) return 'positive';
    if (value >= thresholds.warn) return 'warn';

    return 'negative';
  } else {
    // For metrics where higher values are worse (like packet loss and jitter)
    if (value <= thresholds.positive) return 'positive';
    if (value <= thresholds.warn) return 'warn';

    return 'negative';
  }
}

export function createMetricData({
  type,
  value,
  direction,
}: {
  type: 'mos' | 'packetLoss' | 'jitter';
  value: number;
  direction?: 'inbound' | 'outbound';
}): MetricValue {
  const roundedValue = type === 'mos' ? Number(value.toFixed(1)) : Number(value.toFixed(0));
  const sentiment = calculateMetricSentiment(type, roundedValue);
  return {
    sentiment,
    direction,
    label: `${roundedValue}${metricThresholds[type].suffix}`,
  };
}

export function formatDuration(duration: number): string {
  return duration < 60000
    ? `${Math.floor(duration / 1000)}s`
    : duration < 3600000
    ? dayjs.duration(duration, 'ms').format('m[m] s[s]')
    : dayjs.duration(duration, 'ms').format('H[h] m[m] s[s]');
}
